import {
  CloseOutlined,
  FileSearchOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import { Select } from 'antd'
import { useState } from 'react'

import AtomTreeSelect from '@/components/Atoms/AtomTreeSelect/AtomTreeSelect'
import HoverElement from '@/components/Atoms/HoverElement/HoverElement'
import Loader from '@/components/Atoms/Loader/Loader'
import CharacteristicDetailsModal from '@/pages/NomenclatureEditor/shared/CharacteristicDetailsModal/CharacteristicDetailsModal'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import type { CharacteristicValueType } from '@/types'
import {
  getApprovalStatusLabel,
  isApprovedStatus,
  isCatalogApprover,
} from '@/utils/catalog/approvalStatus'
import { getBarcodeError, sanitizeBarcode } from '@/utils/nomenclature/barcode'
import {
  isSlottedCharacteristicType,
  isUniformSlottedEligible,
} from '@/utils/search/characteristics/slottedCharacteristics'

import { useAttributeProcessingContext } from '../../../AttributeProcessingContext'
import EmptyCharacteristicsFilter from '../EmptyCharacteristicsFilter/EmptyCharacteristicsFilter'
import SlottedFilterInput from '../SlottedFilterInput/SlottedFilterInput'
import TriStateToggle from '../TriStateToggle/TriStateToggle'
import cn from './FiltersPanel.module.scss'

type CharacteristicCardItem = {
  id: string
  code: number
  description: string
  status: 'DRAFT' | 'APPROVED' | 'REJECTED'
  valueType: CharacteristicValueType
  useUnitOfMeasurement: boolean
  unitOfMeasurementDto: null
}

function FiltersPanel() {
  const {
    barcode,
    setBarcode,
    brandId,
    setBrandId,
    brands,
    brandsLoading,
    loadBrands,
    categoryId,
    setCategoryId,
    loadCategories,
    globalCategoriesTree,
    antdCategoriesTreeLeafOnly,
    parentId,
    setParentId,
    loadParents,
    globalGroupsTree,
    antdParentsTreeLeafOnly,
    countryIds,
    setCountryIds,
    countries,
    countriesLoading,
    loadCountries,
    unitIds,
    setUnitIds,
    units,
    unitsLoading,
    loadUnits,
    isDiscontinued,
    setIsDiscontinued,
    isFilled,
    setIsFilled,
    multiplicity,
    setMultiplicity,
    emptyCharacteristicsFilter,
    setEmptyCharacteristicsFilter,
    activeChrcFilters,
    updateChrcFilter,
    removeCharacteristicFilter,
    addFilterOpen,
    setAddFilterOpen,
    characteristicsLoading,
    characteristicsLoadedRef,
    availableCharacteristics,
    usedChrcCodes,
    addCharacteristicFilter,
    loadCharacteristicsTree,
    refreshCharacteristicsTree,
    hasActiveFilters,
    resetAllFilters,
  } = useAttributeProcessingContext()

  const currentUser = useUserInfoStore((state) => state.currentUser)
  const isApprover = isCatalogApprover(currentUser?.roles)
  const isUniformSlottedActive = activeChrcFilters.some(
    (filter) =>
      isSlottedCharacteristicType(filter.type) &&
      filter.matchUniform === true &&
      isUniformSlottedEligible(filter.chosenOptions)
  )
  const [chrcCardItem, setChrcCardItem] =
    useState<CharacteristicCardItem | null>(null)

  const openCharacteristicCard = (code: number) => {
    const chrc = availableCharacteristics.find((c) => c.code === code)
    if (!chrc) return

    setChrcCardItem({
      id: chrc.id,
      code: chrc.code,
      description: chrc.name,
      status: chrc.status ?? 'APPROVED',
      valueType: chrc.type,
      useUnitOfMeasurement: false,
      unitOfMeasurementDto: null,
    })
  }

  const renderCharacteristicOption = (
    characteristic: (typeof availableCharacteristics)[number]
  ) => {
    const isApproved = isApprovedStatus(characteristic.status)
    const statusLabel = getApprovalStatusLabel(characteristic.status)

    return {
      value: characteristic.code,
      label: (
        <span className={cn.chrcOption}>
          <span
            className={[
              cn.chrcOptionDot,
              isApproved ? cn.chrcOptionDotApproved : cn.chrcOptionDotBlocked,
            ].join(' ')}
          />
          <span className={cn.chrcOptionTextWrap}>
            <span className={cn.chrcOptionText}>{characteristic.name}</span>
            {statusLabel && (
              <span className={cn.chrcOptionStatus}>{statusLabel}</span>
            )}
          </span>
        </span>
      ),
      searchText: characteristic.name,
      disabled: !isApproved,
    }
  }

  return (
    <div className={cn.filtersPanel}>
      <div className={cn.filterRow}>
        <label>Штрихкод</label>
        <input
          type="text"
          className={cn.filterTextInput}
          placeholder="Введите штрихкод..."
          value={barcode}
          onChange={(e) => setBarcode(sanitizeBarcode(e.target.value))}
        />
      </div>
      {getBarcodeError(barcode) && (
        <span className={cn.filterError}>{getBarcodeError(barcode)}</span>
      )}

      <div className={cn.filterRow}>
        <label>Бренды</label>
        <Select
          className={cn.filterSelect}
          placeholder="Все бренды"
          value={brandId || undefined}
          onChange={(v) => setBrandId(v ?? '')}
          onOpenChange={(open) => {
            if (open) loadBrands()
          }}
          loading={brandsLoading}
          notFoundContent={brandsLoading ? <Loader size={18} /> : undefined}
          allowClear
          showSearch
          filterOption={(input, opt) =>
            (opt?.label as string)
              ?.toLowerCase()
              .includes(input.trim().toLowerCase())
          }
          options={brands.map((b) => ({ value: b.id, label: b.description }))}
        />
      </div>

      <div className={cn.filterRow}>
        <label>Категории</label>
        <HoverElement>
          <AtomTreeSelect
            tree={globalCategoriesTree}
            antdTree={antdCategoriesTreeLeafOnly}
            openByHover={false}
            value={categoryId}
            className={cn.filterSelect}
            placeholder="Все категории"
            onOpen={loadCategories}
            handleChange={(_, v) => setCategoryId(v ?? '')}
            handleClear={() => setCategoryId('')}
          />
        </HoverElement>
      </div>

      <div className={cn.filterRow}>
        <label>Группы номенклатуры</label>
        <HoverElement>
          <AtomTreeSelect
            tree={globalGroupsTree}
            antdTree={antdParentsTreeLeafOnly}
            openByHover={false}
            value={parentId}
            className={cn.filterSelect}
            placeholder="Все группы"
            onOpen={loadParents}
            handleChange={(_, v) => setParentId(v ?? '')}
            handleClear={() => setParentId('')}
          />
        </HoverElement>
      </div>

      <div className={cn.filterRow}>
        <label>Страна происхождения</label>
        <Select
          mode="multiple"
          className={cn.filterSelect}
          placeholder="Все страны"
          value={countryIds}
          onChange={setCountryIds}
          onOpenChange={(open) => {
            if (open) loadCountries()
          }}
          loading={countriesLoading}
          notFoundContent={countriesLoading ? <Loader size={18} /> : undefined}
          showSearch
          filterOption={(input, opt) =>
            (opt?.label as string)
              ?.toLowerCase()
              .includes(input.trim().toLowerCase())
          }
          options={countries.map((c) => ({
            value: c.id,
            label: c.description,
          }))}
          maxTagCount="responsive"
        />
      </div>

      <div className={cn.filterRow}>
        <label>Единица измерения</label>
        <Select
          mode="multiple"
          className={cn.filterSelect}
          placeholder="Все ед. изм."
          value={unitIds}
          onChange={setUnitIds}
          onOpenChange={(open) => {
            if (open) loadUnits()
          }}
          loading={unitsLoading}
          notFoundContent={unitsLoading ? <Loader size={18} /> : undefined}
          showSearch
          filterOption={(input, opt) =>
            (opt?.label as string)
              ?.toLowerCase()
              .includes(input.trim().toLowerCase())
          }
          options={units.map((u) => ({ value: u.id, label: u.description }))}
          maxTagCount="responsive"
        />
      </div>

      <div className={cn.filterRow}>
        <label>Состояние</label>
        <div className={cn.triToggleGroup}>
          <TriStateToggle
            label="Снят с пр-ва"
            value={isDiscontinued}
            onChange={setIsDiscontinued}
          />
          <TriStateToggle
            label="Заполнен"
            value={isFilled}
            onChange={setIsFilled}
          />
          <TriStateToggle
            label="Кратность"
            value={multiplicity}
            onChange={setMultiplicity}
          />
        </div>
      </div>

      <EmptyCharacteristicsFilter
        value={emptyCharacteristicsFilter}
        characteristics={availableCharacteristics}
        loading={characteristicsLoading}
        onOpen={loadCharacteristicsTree}
        onChange={setEmptyCharacteristicsFilter}
      />

      {activeChrcFilters.length > 0 && (
        <div
          className={[
            cn.chrcFiltersBlock,
            activeChrcFilters.length > 2 ? cn.chrcFiltersScroll : '',
          ].join(' ')}
        >
          {activeChrcFilters.map((f) => (
            <div
              key={f.id}
              className={[
                cn.filterRow,
                isSlottedCharacteristicType(f.type) ? cn.filterRowStacked : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <label title={f.name}>{f.name}</label>
              <div className={cn.chrcFilterInputWrap}>
                {f.type === 'NUMERIC' ? (
                  <div className={cn.rangeInputs}>
                    <input
                      type="number"
                      className={cn.filterTextInput}
                      placeholder={f.unit ? `От, ${f.unit}` : 'От'}
                      value={f.rangeMin}
                      onChange={(e) =>
                        updateChrcFilter(f.id, { rangeMin: e.target.value })
                      }
                    />
                    <span className={cn.rangeSep}>—</span>
                    <input
                      type="number"
                      className={cn.filterTextInput}
                      placeholder={f.unit ? `До, ${f.unit}` : 'До'}
                      value={f.rangeMax}
                      onChange={(e) =>
                        updateChrcFilter(f.id, { rangeMax: e.target.value })
                      }
                    />
                  </div>
                ) : f.type === 'REFERENCE' ? (
                  <Select
                    mode="multiple"
                    className={cn.filterSelect}
                    placeholder="Выберите значения..."
                    value={f.chosenOptions}
                    onChange={(v) =>
                      updateChrcFilter(f.id, { chosenOptions: v })
                    }
                    loading={f.optionsLoading}
                    notFoundContent={
                      f.optionsLoading ? <Loader size={18} /> : undefined
                    }
                    options={(f.referenceOptions ?? []).map((o) => ({
                      value: o.value,
                      label: o.value,
                    }))}
                    maxTagCount="responsive"
                  />
                ) : isSlottedCharacteristicType(f.type) ? (
                  <SlottedFilterInput
                    filter={f}
                    uniformChecked={isUniformSlottedActive}
                    onChange={(patch) => updateChrcFilter(f.id, patch)}
                  />
                ) : f.type === 'BOOLEAN' ? (
                  <Select
                    mode="multiple"
                    className={cn.filterSelect}
                    placeholder="Выберите значения..."
                    value={f.chosenOptions}
                    onChange={(v) =>
                      updateChrcFilter(f.id, { chosenOptions: v })
                    }
                    options={[
                      { value: 'true', label: 'Да' },
                      { value: 'false', label: 'Нет' },
                    ]}
                    maxTagCount="responsive"
                  />
                ) : (
                  <input
                    type="text"
                    className={cn.filterTextInput}
                    placeholder="Введите значение..."
                    value={f.chosenOptions[0] ?? ''}
                    onChange={(e) =>
                      updateChrcFilter(f.id, {
                        chosenOptions: e.target.value ? [e.target.value] : [],
                      })
                    }
                  />
                )}
                <button
                  className={cn.openCardBtn}
                  onClick={() => openCharacteristicCard(f.code)}
                  title="Открыть карточку характеристики"
                >
                  <FileSearchOutlined />
                </button>
                <button
                  className={cn.removeFilterBtn}
                  onClick={() => removeCharacteristicFilter(f.id)}
                  title="Удалить фильтр"
                >
                  <CloseOutlined />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className={cn.filterActions}>
        {addFilterOpen ? (
          <Select
            autoFocus
            className={cn.addFilterSelect}
            placeholder="Выберите характеристику..."
            showSearch
            loading={characteristicsLoading}
            notFoundContent={
              characteristicsLoading ? <Loader size={18} /> : undefined
            }
            filterOption={(input, opt) =>
              String(opt?.searchText || '')
                .toLowerCase()
                .includes(input.trim().toLowerCase())
            }
            options={availableCharacteristics
              .filter((c) => !usedChrcCodes.has(c.code))
              .map(renderCharacteristicOption)}
            onChange={(code) => addCharacteristicFilter(code as number)}
            onBlur={() => setAddFilterOpen(false)}
            open
          />
        ) : (
          <button
            className={cn.addFilterBtn}
            onClick={() => {
              loadCharacteristicsTree()
              setAddFilterOpen(true)
            }}
            disabled={
              !characteristicsLoading &&
              characteristicsLoadedRef.current &&
              availableCharacteristics.filter((c) => !usedChrcCodes.has(c.code))
                .length === 0
            }
          >
            <PlusOutlined />
            Добавить фильтр по характеристике
          </button>
        )}

        <button
          className={cn.btnReset}
          onClick={resetAllFilters}
          disabled={!hasActiveFilters}
        >
          Сбросить всё
        </button>
      </div>

      <CharacteristicDetailsModal
        open={chrcCardItem !== null}
        item={chrcCardItem}
        onClose={() => setChrcCardItem(null)}
        onChanged={() => {
          void refreshCharacteristicsTree()
        }}
        isApprover={isApprover}
      />
    </div>
  )
}

export default FiltersPanel
